import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SignupFormTemplate } from "@/components/signup/SignupFormTemplate.tsx";
import { FormCheckbox } from "@/components/custom/form/FormCheckbox.tsx";
import { FormInput } from "@/components/custom/form/FormInput.tsx";
import { FormCountrySelect } from "@/components/custom/form/FormCountrySelect.tsx";
import { signupFormSchemaTwo } from "@/schemas/signupFormSchema.ts";

interface SignupSecondSectionProps {
    onSubmit: (data: {
        firstName: string;
        lastName: string;
        country?: string;
        postalCode?: string;
        city?: string;
        region?: string;
        street?: string;
        company: boolean;
        companyName?: string;
        vatNumber?: string;
    }) => void;
    isLoading?: boolean;
}

export function SignupFormTwo(props: SignupSecondSectionProps) {
    const form = useForm<z.infer<typeof signupFormSchemaTwo>>({
        resolver: zodResolver(signupFormSchemaTwo),
        defaultValues: {
            firstName: "",
            lastName: "",
            country: "",
            postalCode: "",
            city: "",
            region: "",
            street: "",
            company: false,
            companyName: "",
            vatNumber: "",
        },
    });

    async function onSubmit(data: z.infer<typeof signupFormSchemaTwo>) {
        props.onSubmit(data);
    }

    const isCompany = form.watch("company");

    return (
        <SignupFormTemplate
            form={form}
            onSubmit={onSubmit}
            submitButtonText="Create Account"
            isLoading={props.isLoading}>
            <p className="bg-warning-foreground/60 rounded-2xl p-2">
                In the Early Access phase, billing information is{" "}
                <strong>optional</strong>. If you leave them out here, you may
                have to add them later.
            </p>
            <div className="flex gap-4">
                <FormInput
                    form={form}
                    name="firstName"
                    label="First name"
                    required
                    disableError
                />
                <FormInput
                    form={form}
                    name="lastName"
                    label="Last name"
                    required
                    disableError
                />
            </div>
            <FormCountrySelect
                form={form}
                name="country"
                label="Country"
                disableError
            />
            <div className="flex gap-4">
                <FormInput
                    form={form}
                    name="postalCode"
                    label="Postal code"
                    type="text"
                    inputMode="numeric"
                    onlyDigits
                    disableError
                />
                <FormInput form={form} name="city" label="City" disableError />
            </div>
            <FormInput form={form} name="region" label="Region" disableError />
            <FormInput form={form} name="street" label="Street" disableError />
            <FormCheckbox
                form={form}
                name="company"
                label="Register as a company"
            />
            {isCompany && (
                <>
                    <FormInput
                        form={form}
                        name="companyName"
                        label="Company name"
                        required
                        disableError
                    />
                    <FormInput
                        form={form}
                        name="vatNumber"
                        label="VAT number"
                        required
                        disableError
                    />
                </>
            )}
        </SignupFormTemplate>
    );
}
